import { useSelector } from 'react-redux';
import { TOwnershipStructure } from './types.ts';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { TIdentificationForm, TUpdatePayload } from '~/store/identification-form/types.ts';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.ts';
import { useOwnershipStructureForm } from './hooks/useOwnershipStructureForm.ts';
import { Box, Button } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices.ts';
import { cloneDeep } from 'lodash';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { B2fullCheck, trimValues } from '../../utils.ts';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { gerParsedValues } from './utils/utils.tsx';
import { OwnershipStructure } from './components/OwnershipStructure.tsx';
import { ImportOwnersModal } from './components/ImportOwners/ImportOwnersModal.tsx';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import {
  selectApplicationId,
  selectIdentification,
  selectIsControllingPersonTaxResidentOtherJurisdiction,
  selectIsOtherTaxResident,
} from '~/store/identification-form/selectors.ts';
import {
  taxResidencyAdditionalInfoPrefill,
  updateAdditionalInformation,
} from '~/store/identification-form/thunks/additionalInformationThunks.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';
import { styles } from '~/modules/identification/styles.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { testDataComplexOwnershipStructure } from '~/modules/identification/forms/OwnershipStructure/constants.ts';

interface IComplexOwnershipStructureProps {
  onNextStep: (isNextDisabled?: boolean) => void;
}

export const ComplexOwnershipStructure = ({ onNextStep }: IComplexOwnershipStructureProps) => {
  const dispatch = useAppDispatch();
  const appId = useSelector(selectApplicationId);
  const { isReidentification } = useSelector(selectIdentification) || {};
  const isOtherTaxResident = useSelector(selectIsOtherTaxResident);
  const isControllingPersonTaxResidentOtherJurisdiction = useSelector(
    selectIsControllingPersonTaxResidentOtherJurisdiction
  );
  const { isTestEnvironmentEnabled } = useFeatureFlags();
  const form = useOwnershipStructureForm();
  const { isSubmitting, isDirty } = form.formState;

  const handleFillTestData = () =>
    form.reset(testDataComplexOwnershipStructure, { keepDefaultValues: true });

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TOwnershipStructure) => {
      const parsedValues: TOwnershipStructure = gerParsedValues(values);
      const isBeneficiariesAbsent = !parsedValues.owners.some((item) => item.isBeneficiary);

      if (isDirty) {
        const data: TUpdatePayload = {
          applicationId: appId as number,
          ownershipStructure: trimValues({ ...parsedValues, isBeneficiariesAbsent: undefined }),
        };

        try {
          const callbackFunc = async () => {
            if (!isBeneficiariesAbsent) {
              await dispatch(updateAdditionalInformation({ applicationId: appId! }));
            }
            if (isOtherTaxResident || isControllingPersonTaxResidentOtherJurisdiction) {
              await dispatch(
                taxResidencyAdditionalInfoPrefill({
                  applicationId: appId!,
                  formId: IdentificationFormSteps.ownershipStructure,
                })
              );
            }
          };

          await dispatch(
            updateForm({
              data,
              reportId: IdentificationFormSteps.ownershipStructure,
              callbackFunc,
            })
          );

          await B2fullCheck(appId || '', IdentificationFormSteps.ownershipStructure);

          const formData = {
            field: IdentificationFormStepNames.ownershipStructure as keyof TIdentificationForm,
            data: cloneDeep({
              ...parsedValues,
              isBeneficiariesAbsent,
            }),
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep(isBeneficiariesAbsent);
        } catch (error) {
          console.error(error);
        }
      } else {
        !disableGoNext && onNextStep(isBeneficiariesAbsent);
      }
    };

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        {isTestEnvironmentEnabled && (
          <Box sx={styles.testEnvironmentButton}>
            <Button variant={'outlined'} onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        <SetDirtyModalValues saveValues={form.handleSubmit(onSubmit(isDirty), onInvalidForm)} />

        <Box sx={{ ...commonStyles.columnFlexContainer, gap: 3 }}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <OwnershipStructure />

          <Button
            disabled={isSubmitting}
            type="submit"
            variant="contained"
            sx={{ alignSelf: 'center' }}
            data-testid="contained"
          >
            Продовжити
          </Button>
        </Box>
        {isSubmitting && <FullPageLoader />}
        <ImportOwnersModal />
      </Box>
    </FormProvider>
  );
};
